import dayjs from 'dayjs';

import type { RentalRevenue, RevenueByKind, RevenueGrain, RevenueRow } from '../api/reports';
import { rangeWords } from './garmentMovement';

/**
 * Report C2, rental revenue — board D of canvas "Rental revenue" (owner, 2026-10-30): A's register
 * by period, B's category and design, C's comparison with last year, the twelve-month strip and the
 * split by branch, on one screen under one period picker. Every amount is the server's
 * (`GET /api/report/rental-revenue`); this picks the periods, words the figures and lays out the
 * paper and the sheet.
 */

/** The server's limits (`RentalRevenueService`): a row per day reads a quarter, the report five years. */
export const maxDayRows = 92;
export const maxRevenueDays = 1827;

export type PeriodChoice = 'thisMonth' | 'lastMonth' | 'thisYear' | 'lastYear' | 'custom';

/** The chips on the band, in their order. */
export const periodWords: Record<PeriodChoice, string> = {
  thisMonth: 'This month',
  lastMonth: 'Last month',
  thisYear: 'This year',
  lastYear: 'Last year',
  custom: 'Pick dates…',
};

export const grainWords: Record<RevenueGrain, string> = {
  Day: 'Day',
  Month: 'Month',
  Year: 'Year',
};

const iso = (value: dayjs.Dayjs) => value.format('YYYY-MM-DD');

/** The first day of the financial year (April to March) a day falls in. */
export function financialYearStart(day: string): string {
  const date = dayjs(day);

  return `${date.month() >= 3 ? date.year() : date.year() - 1}-04-01`;
}

/**
 * The range a chip stands for, as of today. This month and this year run to today; last month and
 * last year are whole. Null for the dates the person picks.
 */
export function periodRange(
  choice: PeriodChoice,
  today: string,
): { from: string; to: string } | null {
  const now = dayjs(today);

  switch (choice) {
    case 'thisMonth':
      return { from: iso(now.startOf('month')), to: today };
    case 'lastMonth': {
      const last = now.subtract(1, 'month');

      return { from: iso(last.startOf('month')), to: iso(last.endOf('month')) };
    }
    case 'thisYear':
      return { from: financialYearStart(today), to: today };
    case 'lastYear': {
      const start = dayjs(financialYearStart(today)).subtract(1, 'year');

      return { from: iso(start), to: iso(start.add(1, 'year').subtract(1, 'day')) };
    }
    default:
      return null;
  }
}

/** The row a range reads best by: each day for a month or less, each month beyond. */
export function naturalGrain(from: string, to: string): RevenueGrain {
  return dayjs(to).diff(dayjs(from), 'day') + 1 <= 31 ? 'Day' : 'Month';
}

/** Whether a row per day is on offer for a range — the server refuses more than a quarter of days. */
export function dayRowsAllowed(from: string, to: string): boolean {
  return dayjs(to).diff(dayjs(from), 'day') + 1 <= maxDayRows;
}

/** Why a range would be refused, said before asking. Null when fine. */
export function revenueRangeProblem(from: string, to: string, grain: RevenueGrain): string | null {
  const days = dayjs(to).diff(dayjs(from), 'day') + 1;

  if (days < 1) {
    return 'The last day comes before the first.';
  }

  if (days > maxRevenueDays) {
    return 'The rental revenue report covers at most five years.';
  }

  return grain === 'Day' && days > maxDayRows
    ? 'A row per day covers at most 92 days. Choose a row per month for a longer range.'
    : null;
}

export interface RevenueKindColumn {
  key: keyof RevenueByKind;
  /** The register's heading. */
  label: string;
  /** The same said in full, over its figure. */
  figure: string;
}

/** The kinds of charge, in the register's order. */
export const revenueKinds: readonly RevenueKindColumn[] = [
  { key: 'rent', label: 'Rent', figure: 'Rent (incl. extensions)' },
  { key: 'lateFees', label: 'Late fees', figure: 'Late fees' },
  { key: 'damage', label: 'Damage', figure: 'Damage & accessories' },
  { key: 'alterations', label: 'Alterations', figure: 'Alterations' },
  { key: 'cancellations', label: 'Cancellations', figure: 'Kept on cancellations' },
];

/** A change against last year, or null when there was nothing last year to compare with. */
export function changeOn(now: number, before: number): { pct: number; up: boolean } | null {
  if (before === 0) {
    return null;
  }

  const pct = Math.round((now / before - 1) * 100);

  return { pct: Math.abs(pct), up: pct >= 0 };
}

/** "▲ 23%", "▼ 6%", or a dash where last year had nothing. */
export function changeWords(change: { pct: number; up: boolean } | null): string {
  return change === null ? '—' : `${change.up ? '▲' : '▼'} ${change.pct}%`;
}

export interface RevenueFigure {
  key: string;
  label: string;
  value: number;
  change: { pct: number; up: boolean } | null;
}

/** The figures over the strip: each kind earned, then the GST collected, each against last year. */
export function revenueFigures(report: RentalRevenue): RevenueFigure[] {
  return [
    ...revenueKinds.map((kind) => ({
      key: kind.key,
      label: kind.figure,
      value: report.kinds[kind.key],
      change: changeOn(report.kinds[kind.key], report.lastYearKinds[kind.key]),
    })),
    {
      key: 'gst',
      label: 'GST collected',
      value: report.gst,
      change: changeOn(report.gst, report.lastYearGst),
    },
  ];
}

/** A register row's name: "Fri 23 Oct", "Apr 2026" (or "10–30 Apr 2026" when clipped), "FY 2026-27". */
export function rowLabel(row: Pick<RevenueRow, 'from' | 'to'>, grain: RevenueGrain): string {
  const from = dayjs(row.from);
  const to = dayjs(row.to);

  if (grain === 'Day') {
    return from.format('ddd D MMM');
  }

  if (grain === 'Year') {
    const start = dayjs(financialYearStart(row.from)).year();

    return `FY ${start}-${String(start + 1).slice(-2)}`;
  }

  const whole = from.date() === 1 && to.date() === to.daysInMonth();

  return whole ? from.format('MMM YYYY') : `${from.format('D')}–${to.format('D MMM YYYY')}`;
}

/**
 * The band's sentence: what was earned over the period, against the same days last year when there
 * were any, and the category that brought in most when it is a real category among several.
 */
export function revenueSentence(
  report: RentalRevenue,
  periodName: string | null,
  money: (value: number) => string,
): string {
  const range = rangeWords(report.from, report.to);
  const when = periodName ? `${periodName} (${range})` : range;

  if (report.earned === 0 && report.gst === 0) {
    return `${when}: nothing was earned.`;
  }

  const change = changeOn(report.earned, report.lastYearEarned);
  const against = change
    ? `, ${change.up ? 'up' : 'down'} ${change.pct}% on the same days last year`
    : '';

  const top = report.categories[0];
  const lead =
    top && top.categoryId !== null && report.categories.length > 1 && report.earned > 0
      ? ` ${top.name} brought in ${Math.round((top.earned / report.earned) * 100)}% of it.`
      : '';

  return `${when}: ${money(report.earned)} earned before GST${against}.${lead}`;
}

/** A share of the whole, as a fraction; zero when the whole is nothing. */
export function shareOf(value: number, whole: number): number {
  return whole > 0 ? Math.max(0, value) / whole : 0;
}

/** Earned per garment handed over, or null where none went out. */
export function perHire(earned: number, hires: number): number | null {
  return hires > 0 ? Math.round(earned / hires) : null;
}

/** The twelve bars: each month's height against the best month, and its short name. */
export function twelveBars(
  report: RentalRevenue,
): { month: string; short: string; earned: number; height: number; inPeriod: boolean }[] {
  const best = Math.max(0, ...report.lastTwelveMonths.map((month) => month.earned));

  return report.lastTwelveMonths.map((month) => ({
    month: month.month,
    short: dayjs(month.month).format('MMM'),
    earned: month.earned,
    height: shareOf(month.earned, best),
    inPeriod: month.inPeriod,
  }));
}

/** "4.1L", "86k", "900" — a bar's figure, short enough to sit over it. */
export function shortAmount(value: number): string {
  if (Math.abs(value) >= 100_000) {
    return `${(value / 100_000).toFixed(1)}L`;
  }

  return Math.abs(value) >= 1_000 ? `${Math.round(value / 1_000)}k` : String(Math.round(value));
}

/** The register as a sheet: a row per period with every kind, GST, billed and last year; totals last. */
export function revenueSheet(
  report: RentalRevenue,
  grain: RevenueGrain,
): { headers: string[]; rows: string[][] } {
  const headers = [
    grainWords[grain],
    'From',
    'To',
    ...revenueKinds.map((kind) => kind.label),
    'Before GST',
    'GST',
    'Billed',
    'Last year',
  ];

  const rows = report.rows.map((row) => [
    rowLabel(row, grain),
    row.from,
    row.to,
    ...revenueKinds.map((kind) => String(row.kinds[kind.key])),
    String(row.earned),
    String(row.gst),
    String(row.billed),
    String(row.lastYearEarned),
  ]);

  rows.push([
    'Total',
    report.from,
    report.to,
    ...revenueKinds.map((kind) => String(report.kinds[kind.key])),
    String(report.earned),
    String(report.gst),
    String(report.billed),
    String(report.lastYearEarned),
  ]);

  return { headers, rows };
}
